import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Plus, Download, FileText, Trash2, CheckCircle, Edit, Search, X, Eye, DollarSign, Calendar, ClipboardCheck, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PeriodFilter } from '../components/PeriodFilter';
import { Pagination } from '../components/shared/Pagination';
import { OrderPaymentDialog } from '../components/orders/OrderPaymentDialog';
import { OrderEditDialog } from '../components/orders/OrderEditDialog';
import { OrderPreviewDialog } from '../components/orders/OrderPreviewDialog';
import { ShoppingListDialog } from '../components/orders/ShoppingListDialog';
import { formatNumber, getStatusColor, ORDER_STATUSES, PAYMENT_METHODS } from '../utils/constants';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersPage = () => {
  const { hasPermission } = useAuth();
  const canDelete = hasPermission('delete_order');
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [shoppingListOpen, setShoppingListOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
  const [orderPayments, setOrderPayments] = useState({ payments: [], total_paid: 0, balance_due: 0 });
  const [editingPayment, setEditingPayment] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: '', payment_method: '', payment_date: '' });
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [orderItems, setOrderItems] = useState([]);
  const [editOrderItems, setEditOrderItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState({ startDate: '', endDate: '', label: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [dateEditOrder, setDateEditOrder] = useState(null);
  const [dateEditValue, setDateEditValue] = useState('');
  const ITEMS_PER_PAGE = 25;

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const matchesSearch = searchTerm === '' || 
        order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.order_number && order.order_number.toString().includes(searchTerm));
      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
      const matchesCustomer = customerFilter === 'all' || order.customer_id === customerFilter;
      const od = new Date(order.created_at);
      const matchesDateFrom = !periodFilter.startDate || od >= new Date(periodFilter.startDate);
      const matchesDateTo = !periodFilter.endDate || od <= new Date(periodFilter.endDate + 'T23:59:59');
      return matchesSearch && matchesStatus && matchesCustomer && matchesDateFrom && matchesDateTo;
    });
  }, [orders, searchTerm, statusFilter, customerFilter, periodFilter]);

  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setCustomerFilter('all'); setCurrentPage(1); };
  const hasActiveFilters = searchTerm || statusFilter !== 'all' || customerFilter !== 'all';

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(currentPage, totalPages);
  const paginatedOrders = filteredOrders.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  useEffect(() => { fetchOrders(); fetchCustomers(); }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`);
      setOrders(response.data);
    } catch (error) { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const handleEditDate = (order) => {
    const d = order.created_at ? new Date(order.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setDateEditOrder(order);
    setDateEditValue(d);
  };

  const handleSaveDate = async () => {
    if (!dateEditOrder || !dateEditValue) return;
    try {
      await axios.put(`${API_URL}/orders/${dateEditOrder.id}/date?order_date=${dateEditValue}`);
      toast.success('Order date updated');
      setDateEditOrder(null);
      fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update date'); }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/customers`);
      setCustomers(response.data);
    } catch (error) { toast.error('Failed to load customers'); }
  };

  const fetchPriceList = async (customerId) => {
    try {
      const response = await axios.get(`${API_URL}/price-lists/customer/${customerId}`);
      setPriceList(response.data);
    } catch (error) { toast.error('Failed to load price list'); }
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomer(customerId);
    fetchPriceList(customerId);
    setOrderItems([]);
  };

  const addOrderItem = () => {
    setOrderItems([...orderItems, { product_id: '', product_name: '', quantity: '', unit_price: 0, total: 0, unit: '' }]);
  };

  const updateOrderItem = (index, field, value) => {
    const newItems = [...orderItems];
    newItems[index][field] = value;
    if (field === 'product_id') {
      const product = priceList.find(p => p.product.id === value);
      if (product) {
        newItems[index].product_name = product.product.name;
        newItems[index].unit_price = product.price;
        newItems[index].unit = product.product.unit;
        newItems[index].total = parseFloat(newItems[index].quantity || 0) * product.price;
      }
    }
    if (field === 'quantity') {
      newItems[index].total = parseFloat(value || 0) * newItems[index].unit_price;
    }
    setOrderItems(newItems);
  };

  const removeOrderItem = (index) => { setOrderItems(orderItems.filter((_, i) => i !== index)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer || orderItems.length === 0) { toast.error('Please select a customer and add at least one item'); return; }
    const validItems = orderItems.filter(item => item.product_id && item.quantity > 0);
    if (validItems.length === 0) { toast.error('Please add valid order items'); return; }
    try {
      const orderData = {
        customer_id: selectedCustomer,
        items: validItems.map(item => ({ product_id: item.product_id, product_name: item.product_name, quantity: parseFloat(item.quantity), unit: item.unit || 'kg', unit_price: item.unit_price, total: item.total })),
        tax: 0, notes, order_date: orderDate
      };
      await axios.post(`${API_URL}/orders`, orderData);
      toast.success('Order created successfully');
      setDialogOpen(false);
      resetForm();
      fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create order'); }
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try { await axios.delete(`${API_URL}/orders/${id}`); toast.success('Order deleted successfully'); fetchOrders(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete order'); }
  };

  const resetForm = () => { setSelectedCustomer(''); setOrderItems([]); setNotes(''); setPriceList([]); setOrderDate(new Date().toISOString().split('T')[0]); };

  // Download functions
  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/orders/${orderId}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `invoice_${orderId.substring(0, 8)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Invoice downloaded');
    } catch (error) { toast.error('Failed to download invoice'); }
  };

  const downloadDeliveryNote = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/orders/${orderId}/delivery-note`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `delivery_note_${orderId.substring(0, 8)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Delivery note downloaded');
    } catch (error) { toast.error('Failed to download delivery note'); }
  };

  const downloadOrderExcel = async (order) => {
    try {
      const response = await axios.get(`${API_URL}/orders/${order.id}/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `order_${order.order_number || order.id.substring(0, 8)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Excel downloaded');
    } catch (error) { toast.error('Failed to download Excel'); }
  };

  const downloadFilteredExcel = async () => {
    if (filteredOrders.length === 0) { toast.error('No orders to export'); return; }
    try {
      const response = await axios.post(`${API_URL}/orders/export`, filteredOrders.map(o => o.id), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `orders_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`Exported ${filteredOrders.length} orders`);
    } catch (error) { toast.error('Failed to export Excel'); }
  };

  // Status functions
  const openStatusDialog = (order) => {
    setSelectedOrder(order);
    setStatusUpdate({ status: order.status, payment_method: order.payment_method || '', payment_date: order.payment_date ? order.payment_date.split('T')[0] : new Date().toISOString().split('T')[0] });
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedOrder) return;
    try {
      const params = new URLSearchParams(); params.append('status', statusUpdate.status);
      await axios.put(`${API_URL}/orders/${selectedOrder.id}/status?${params.toString()}`);
      toast.success('Order status updated successfully');
      setStatusDialogOpen(false); setSelectedOrder(null); fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update order status'); }
  };

  // Edit functions
  const openEditDialog = async (order) => {
    if (order.status === 'cancelled') { toast.error('Cannot edit cancelled orders'); return; }
    setEditingOrder(order); setEditNotes(order.notes || '');
    try { const response = await axios.get(`${API_URL}/price-lists/customer/${order.customer_id}`); setPriceList(response.data); }
    catch (error) { console.error('Failed to load price list'); }
    setEditOrderItems(order.items.map(item => ({ product_id: item.product_id, product_name: item.product_name, quantity: item.quantity, unit_price: item.unit_price, unit: item.unit || 'kg', total: item.total })));
    setEditDialogOpen(true);
  };

  const updateEditOrderItem = (index, field, value) => {
    const newItems = [...editOrderItems]; newItems[index][field] = value;
    if (field === 'product_id') {
      const product = priceList.find(p => p.product.id === value);
      if (product) { newItems[index].product_name = product.product.name; newItems[index].unit_price = product.price; newItems[index].unit = product.product.unit; newItems[index].total = parseFloat(newItems[index].quantity || 0) * product.price; }
    }
    if (field === 'quantity') { newItems[index].total = parseFloat(value || 0) * newItems[index].unit_price; }
    setEditOrderItems(newItems);
  };

  const addEditOrderItem = () => { setEditOrderItems([...editOrderItems, { product_id: '', product_name: '', quantity: '', unit_price: 0, total: 0, unit: '' }]); };
  const removeEditOrderItem = (index) => { setEditOrderItems(editOrderItems.filter((_, i) => i !== index)); };

  const handleEditSubmit = async (e) => {
    e.preventDefault(); if (!editingOrder) return;
    const validItems = editOrderItems.filter(item => item.product_id && item.quantity > 0);
    if (validItems.length === 0) { toast.error('Please add at least one valid order item'); return; }
    try {
      const updateData = { items: validItems.map(item => ({ product_id: item.product_id, product_name: item.product_name, quantity: parseFloat(item.quantity), unit: item.unit || 'kg', unit_price: item.unit_price, total: item.total })), notes: editNotes };
      await axios.put(`${API_URL}/orders/${editingOrder.id}`, updateData);
      toast.success('Order updated successfully'); setEditDialogOpen(false); setEditingOrder(null); setEditOrderItems([]); setEditNotes(''); fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update order'); }
  };

  // Preview functions
  const previewInvoice = async (orderId) => {
    try { const response = await axios.get(`${API_URL}/orders/${orderId}/invoice`, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })); setPreviewUrl(url); setPreviewTitle('Invoice Preview'); setPreviewDialogOpen(true); }
    catch (error) { toast.error('Failed to load invoice preview'); }
  };

  const previewDeliveryNote = async (orderId) => {
    try { const response = await axios.get(`${API_URL}/orders/${orderId}/delivery-note`, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })); setPreviewUrl(url); setPreviewTitle('Delivery Note Preview'); setPreviewDialogOpen(true); }
    catch (error) { toast.error('Failed to load delivery note preview'); }
  };

  const downloadFromPreview = () => {
    const link = document.createElement('a'); link.href = previewUrl;
    link.setAttribute('download', `${previewTitle.replace(' Preview', '').replace(' ', '_').toLowerCase()}.pdf`);
    document.body.appendChild(link); link.click(); link.remove(); toast.success('Document downloaded');
  };

  const closePreview = () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); setPreviewUrl(''); setPreviewTitle(''); setPreviewDialogOpen(false); };

  // Payment functions
  const openPaymentDialog = async (order) => {
    setPaymentOrder(order); setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] }); setEditingPayment(null);
    try { const response = await axios.get(`${API_URL}/orders/${order.id}/payments`); setOrderPayments(response.data); }
    catch (error) { setOrderPayments({ payments: [], total_paid: 0, balance_due: order.total }); }
    setPaymentDialogOpen(true);
  };

  const fetchPaymentHistory = async () => {
    if (!paymentOrder) return;
    try { const response = await axios.get(`${API_URL}/orders/${paymentOrder.id}/payments`); setOrderPayments(response.data); }
    catch (error) { console.error('Failed to fetch payment history'); }
  };

  const handleRecordPayment = async () => {
    if (!paymentOrder || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { toast.error('Please enter a valid payment amount'); return; }
    try {
      const params = new URLSearchParams(); params.append('payment_method', paymentForm.payment_method); params.append('amount', paymentForm.amount);
      if (paymentForm.payment_date) params.append('payment_date', paymentForm.payment_date);
      await axios.post(`${API_URL}/orders/${paymentOrder.id}/payment?${params.toString()}`);
      toast.success('Payment recorded successfully'); setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] }); await fetchPaymentHistory(); fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to record payment'); }
  };

  const handleEditPayment = (payment) => { setEditingPayment(payment); setPaymentForm({ amount: payment.amount.toString(), payment_method: payment.payment_method, payment_date: (payment.date || payment.created_at || '').split('T')[0] || new Date().toISOString().split('T')[0] }); };

  const handleUpdatePayment = async () => {
    if (!editingPayment || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { toast.error('Please enter a valid payment amount'); return; }
    try {
      const params = new URLSearchParams(); params.append('amount', paymentForm.amount); params.append('payment_method', paymentForm.payment_method);
      if (paymentForm.payment_date) params.append('payment_date', paymentForm.payment_date);
      await axios.put(`${API_URL}/orders/${paymentOrder.id}/payments/${editingPayment.id}?${params.toString()}`);
      toast.success('Payment updated successfully'); setEditingPayment(null); setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] }); await fetchPaymentHistory(); fetchOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update payment'); }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try { await axios.delete(`${API_URL}/orders/${paymentOrder.id}/payments/${paymentId}`); toast.success('Payment deleted successfully'); await fetchPaymentHistory(); fetchOrders(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete payment'); }
  };

  const cancelEditPayment = () => { setEditingPayment(null); setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] }); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;

  const subtotal = orderItems.reduce((sum, item) => sum + (item.total || 0), 0);
  const total = subtotal;

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Orders</h1>
          <p className="text-muted-foreground">Process customer orders and generate documents</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadFilteredExcel} data-testid="export-orders-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-green-700" />Download Excel
          </Button>
          <Button variant="outline" onClick={() => setShoppingListOpen(true)} data-testid="shopping-list-btn">
            <ClipboardCheck className="w-4 h-4 mr-2" />Shopping list
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" data-testid="create-order-btn">
              <Plus className="w-5 h-5 mr-2" /> Create Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
            <DialogHeader><DialogTitle>Create New Order</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Customer</label>
                <Select value={selectedCustomer} onValueChange={handleCustomerChange}>
                  <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Select a customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (<SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {selectedCustomer && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium">Order Items</label>
                    <Button type="button" size="sm" onClick={addOrderItem} data-testid="add-order-item-btn"><Plus className="w-4 h-4 mr-2" />Add Item</Button>
                  </div>
                  <div className="space-y-3">
                    {orderItems.map((item, index) => (
                      <div key={index} className="flex items-end gap-3 p-4 bg-muted/30 rounded-lg" data-testid="order-item-row">
                        <div className="flex-1">
                          <label className="block text-xs font-medium mb-1">Product</label>
                          <Select value={item.product_id} onValueChange={(value) => updateOrderItem(index, 'product_id', value)}>
                            <SelectTrigger data-testid={`order-item-product-${index}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                            <SelectContent>
                              {priceList.map(p => (<SelectItem key={p.product.id} value={p.product.id}>{p.product.name} ({p.product.unit}) - RWF {p.price}/{p.product.unit}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-28">
                          <label className="block text-xs font-medium mb-1">Qty ({item.unit || 'unit'})</label>
                          <Input type="number" step="0.1" value={item.quantity} onChange={(e) => updateOrderItem(index, 'quantity', e.target.value)} data-testid={`order-item-quantity-${index}`} />
                        </div>
                        <div className="w-32">
                          <label className="block text-xs font-medium mb-1">Total</label>
                          <div className="h-11 px-3 flex items-center bg-background border border-input rounded-md">RWF {formatNumber(item.total)}</div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeOrderItem(index)} data-testid={`remove-order-item-${index}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-primary/10 p-6 rounded-xl mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-foreground">Total Amount</span>
                  <span className="text-3xl font-bold text-primary" data-testid="order-total">RWF {formatNumber(total)}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Order Date</label>
                  <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} data-testid="order-date-input" />
                  <p className="text-xs text-muted-foreground mt-1">Change to backdate this order</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="order-notes-input" />
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="order-submit-btn">Create Order</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ShoppingListDialog open={shoppingListOpen} onOpenChange={setShoppingListOpen} />

      <PeriodFilter onFilterChange={setPeriodFilter} />

      <div className="bg-white rounded-xl border border-border shadow-sm p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by customer name or order ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="order-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter"><SelectValue placeholder="Filter by status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {ORDER_STATUSES.map(status => (<SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="w-[200px]" data-testid="customer-filter"><SelectValue placeholder="Filter by customer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {hasActiveFilters && (<Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn"><X className="w-4 h-4 mr-1" />Clear</Button>)}
        </div>
        {(hasActiveFilters || periodFilter.label) && (
          <div className="text-sm text-muted-foreground">
            Showing {filteredOrders.length} of {orders.length} orders
            {periodFilter.label && <span className="ml-2">• Period: {periodFilter.label}</span>}
            {searchTerm && <span className="ml-2">• Search: "{searchTerm}"</span>}
            {statusFilter !== 'all' && <span className="ml-2 capitalize">• Status: {statusFilter}</span>}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="orders-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Order #</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Customer</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Date</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Total</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Paid</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Balance</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Status</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredOrders.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">
                  {orders.length === 0 ? 'No orders yet. Create your first order to get started.' : 'No orders match your search criteria.'}
                </td></tr>
              )}
              {paginatedOrders.map((order) => (
                <tr key={order.id} className="hover:bg-muted/30 transition-colors" data-testid="order-row">
                  <td className="px-6 py-4 text-sm font-mono font-medium text-foreground">{order.order_number || order.id.substring(0, 8).toUpperCase()}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{order.customer_name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-foreground">RWF {formatNumber(order.total)}</td>
                  <td className="px-6 py-4 text-sm text-emerald-600">{formatNumber(order.status === 'paid' ? order.total : (order.amount_paid || 0))}</td>
                  <td className="px-6 py-4 text-sm font-medium">
                    {order.status === 'paid' ? (<span className="text-emerald-600">Paid</span>
                    ) : (order.balance_due !== undefined ? order.balance_due : order.total - (order.amount_paid || 0)) > 0 ? (
                      <span className="text-amber-600">RWF {formatNumber(order.balance_due !== undefined ? order.balance_due : order.total - (order.amount_paid || 0))}</span>
                    ) : (<span className="text-emerald-600">Paid</span>)}
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => openStatusDialog(order)} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(order.status)}`} data-testid="order-status-btn">
                      {order.status}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      {order.status !== 'cancelled' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openPaymentDialog(order)} data-testid="record-payment-btn" title="Record Payment"><DollarSign className="w-4 h-4 text-emerald-600" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(order)} data-testid="edit-order-btn" title="Edit Order"><Edit className="w-4 h-4 text-blue-600" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditDate(order)} data-testid="edit-date-btn" title="Edit Date"><Calendar className="w-4 h-4 text-amber-600" /></Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openStatusDialog(order)} data-testid="update-status-btn" title="Update Status"><CheckCircle className="w-4 h-4 text-primary" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => previewInvoice(order.id)} data-testid="preview-invoice-btn" title="Preview Invoice"><Eye className="w-4 h-4 mr-1 text-blue-600" />Invoice</Button>
                      <Button variant="ghost" size="sm" onClick={() => previewDeliveryNote(order.id)} data-testid="preview-delivery-btn" title="Preview Delivery Note"><Eye className="w-4 h-4 mr-1 text-green-600" />Delivery</Button>
                      <Button variant="ghost" size="sm" onClick={() => downloadOrderExcel(order)} data-testid="download-order-excel-btn" title="Download Excel"><FileSpreadsheet className="w-4 h-4 text-green-700" /></Button>
                      {canDelete && (<Button variant="ghost" size="sm" onClick={() => handleDeleteOrder(order.id)} data-testid="delete-order-btn"><Trash2 className="w-4 h-4 text-destructive" /></Button>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={currentPage} pages={totalPages} total={filteredOrders.length} limit={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </div>

      {/* Extracted Dialog Components */}
      <OrderPreviewDialog open={previewDialogOpen} onClose={closePreview} previewUrl={previewUrl} previewTitle={previewTitle} onDownload={downloadFromPreview} />

      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent data-testid="status-update-dialog">
          <DialogHeader><DialogTitle>Update Order Status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium mb-2">Order #</label><p className="text-sm font-mono bg-muted/30 p-2 rounded">{selectedOrder?.order_number || selectedOrder?.id?.substring(0, 8).toUpperCase()}</p></div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <Select value={statusUpdate.status} onValueChange={(value) => setStatusUpdate({ ...statusUpdate, status: value })}>
                <SelectTrigger data-testid="status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>{ORDER_STATUSES.map(status => (<SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <Button onClick={handleStatusUpdate} className="w-full" data-testid="status-submit-btn">Update Status</Button>
          </div>
        </DialogContent>
      </Dialog>

      <OrderEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        editingOrder={editingOrder}
        editOrderItems={editOrderItems}
        editNotes={editNotes}
        setEditNotes={setEditNotes}
        priceList={priceList}
        onUpdateItem={updateEditOrderItem}
        onAddItem={addEditOrderItem}
        onRemoveItem={removeEditOrderItem}
        onSubmit={handleEditSubmit}
        onClose={() => { setEditingOrder(null); setEditOrderItems([]); setEditNotes(''); }}
      />

      <OrderPaymentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        paymentOrder={paymentOrder}
        orderPayments={orderPayments}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        editingPayment={editingPayment}
        onRecordPayment={handleRecordPayment}
        onEditPayment={handleEditPayment}
        onUpdatePayment={handleUpdatePayment}
        onDeletePayment={handleDeletePayment}
        onCancelEdit={cancelEditPayment}
      />

      {/* Date Edit Dialog */}
      <Dialog open={!!dateEditOrder} onOpenChange={(open) => { if (!open) setDateEditOrder(null); }}>
        <DialogContent data-testid="edit-date-dialog">
          <DialogHeader>
            <DialogTitle>Edit Order Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Order #{dateEditOrder?.order_number} — {dateEditOrder?.customer_name}
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">Order Date</label>
              <Input
                type="date"
                value={dateEditValue}
                onChange={(e) => setDateEditValue(e.target.value)}
                data-testid="order-date-edit-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveDate} className="flex-1" data-testid="save-date-btn">Save Date</Button>
              <Button variant="outline" onClick={() => setDateEditOrder(null)} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
